import { useRef, useState, useEffect, useCallback, lazy, Suspense } from 'react';
import classnames from 'classnames';
import { renderRoutes } from 'react-router-config';
import { RouteComponentProps } from 'react-router';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import { SectionProvider } from '../Sections/SectionContext';
import { ThemeOptions, ThemeSkin, ThemeOverride } from '@wix/da-react-context/pkg/ThemeContext';
import { Promo } from '@wix/da-react-context/pkg/publicSession';
import GruserContext from '@wix/da-gruser-shared/pkg/context/GruserContext';
import { Gruser } from '@wix/da-gruser-shared/pkg/types/papi/';
import SiteHeader from '@wix/da-shared-react/pkg/SiteHeader';
import SiteFooter from '@wix/da-shared-react/pkg/SiteFooter';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import DrawerLoadable from '@wix/da-feedback-shared/pkg/Drawer/DrawerLoadable';
import FeedbackPageLoadable from '@wix/da-feedback-shared/pkg/FeedbackPage/FeedbackPageLoadable';
import SaleBanner from '@wix/da-shared-react/pkg/banners/SaleBanner';
import ProfileIdentity from '../UserWidgets/ProfileIdentity';
import SubNavBar from '../SubNavBar';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import ProfileHero from '../UserWidgets/ProfileHero';
import ProfileHeroMobile from '../UserWidgets/ProfileHeroMobile';
import MonetizationPromoBanner from '../UserWidgets/MonetizationPromoBanner';
import DraggableProfileHero from '../UserWidgets/ProfileHero/DraggableProfileHero';
import ProfileHeroEditControls from '../UserWidgets/ProfileHero/ProfileHeroEditControls';
import { routes } from '../../routes';
import { ProfileSkin } from '../../../types/profileSkins';
import { PageType } from '../../../types/pageType';
import BackgroundContainer from './BackgroundContainer';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import ConditionalWrapper from '@wix/da-ds/pkg/ConditionalWrapper';
import modalRenderer from '../Modals';
import { composeModalRenderers } from '@wix/da-shared-react/pkg/Modals/helpers';
import { adoptsModalRenderer } from '@wix/da-adopts-shared/pkg/project-tools';
import { submitModalRenderer } from '@wix/da-submit-shared/pkg/project-tools';
import { muroModalRenderer } from '@wix/da-muro-ui/pkg/project-tools';
import { feedbackModalRenderer } from '@wix/da-feedback-shared/pkg/project-tools';
import PWAInstallBanner from '@wix/da-shared-react/pkg/PWAInstallBanner';
import GroupCreation from '../Pages/GroupCreation';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import MicroMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Micro';
import MiniMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mini';
import MobileMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mobile';
import DamzDrawerLoadable from '@wix/da-damz-shared/pkg/components/Drawer/DrawerLoadable';
import DamzPageLoadable from '@wix/da-damz-shared/pkg/components/DamzPage/DamzPageLoadable';
import DamzGlobal from '@wix/da-damz-shared/pkg/redux/ModuleLoader';
import { damzModalRenderer } from '@wix/da-damz-shared/pkg/project-tools';

const MuroDepValue = {
  MicroComponent: MicroMuro,
  MiniComponent: MiniMuro,
  MobileComponent: MobileMuro,
};

import s from './App.scss';

const ProfileSkinSelector = lazy(
  () => import(/* webpackChunkName: "owner" */ '../ProfileSkinSelector')
);

export interface Props {
  activeSection: string;
  language: string;
  isMobile: boolean;
  isOwner?: boolean;
  isAuthorizedToCustomize?: boolean;
  profileOwner: PapiUser;
  isSectionLoading?: boolean;
  theme?: ThemeOptions;
  themeSkin?: ThemeSkin;
  coverDeviation?: PapiDeviation;
  userAgent?: string;
  environment?: RuntimeEnvironment;
  showProfileHeroAid?: boolean;
  isHeroEditingActive?: boolean;
  isFeedbackPageOpen?: boolean;
  PWAInstallBannerEligible?: boolean;
  appUrl: string;
  pageType?: PageType;
  promos?: Promo[];
  onAppRendered(): void;
  onPathChange(location: object): void;
  onInitFallbackBadgeCheck(): void;

  gruser: Gruser;

  /**
   * We set up some CSS classes, disable some ThemeOverrides, and more if the profile owner has a profile skin active.
   */
  profileSkin?: ProfileSkin;
  isSkinSelectorShown?: boolean;
  isDuperbrowseActive?: boolean;
  hasBadgesInstalled: boolean;
  flags: string[];
}

function isProfileSkinActive(profileSkin?: ProfileSkin, isDuperbrowseActive?: boolean) {
  return profileSkin && profileSkin.skinId !== 'official' && !isDuperbrowseActive;
}

function App(props: Props & RouteComponentProps<any>) {
  const [, setReducedMotion] = useState(false);
  const pageContentContainer = useRef<any>(null);
  const sectionContentContainer = useRef<any>(null);
  const doneShowingEditControls = useRef(false);

  const {
    language = 'en',
    environment,
    appUrl,
    isHeroEditingActive,
    pageType,
    gruser,
    profileOwner,
    profileSkin,
    themeSkin,
    PWAInstallBannerEligible,
    onAppRendered,
    onPathChange,
    location,
    promos,
    isOwner,
    isMobile,
    isAuthorizedToCustomize,
    showProfileHeroAid,
    isSkinSelectorShown,
    activeSection,
    coverDeviation,
    theme,
  } = props;

  useEffect(() => {
    onAppRendered();
    if (pageType !== 'profile') {
      return;
    }
    const pageContainerClassList = pageContentContainer.current?.classList;
    pageContainerClassList?.add(s['show-edit-controls']);
    // on SSR rendering we show edit controls
    // and hide them after 2 seconds here using direct node manipulation
    // to be make it more performant
    // we also need to set this.doneShowingEditControls so that
    // a subsequent re-render doesn't make them re-appear
    const timer = setTimeout(() => {
      pageContainerClassList?.remove(s['show-edit-controls']);
      doneShowingEditControls.current = true;
      // this is not done via state to not re-render App.tsx in a bit after page load
      // while the user is likely to be scrolling.
      // We track isFeedbackPageOpen flag so that the App is re-rendered when we
      // toggle the feedback page, otherwise edit controls are never disabled.
    }, 2000);
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (mql.matches) {
        setReducedMotion(true);
      }
    }
    return () => clearTimeout(timer);
  }, [onAppRendered, pageType]);

  const prevLocationRef = useRef(location);
  useEffect(() => {
    if (pageType !== 'profile') return;
    const prev = prevLocationRef.current;
    const section = prev.pathname.split('/')[2];
    const nextSection = location.pathname.split('/')[2];
    if (['gallery', 'favourites', 'private-collections', 'subscriptions'].includes(section)) {
      if (section !== nextSection) {
        onPathChange(location);
      } else if (prev.search && !location.search) {
        // Treat returning to section homepage as a section change
        onPathChange(location);
      }
    } else if (location.pathname !== prev.pathname) {
      // Using not full url to make navigation to <a name /> by hash works
      // or passing params to section data loader
      onPathChange(location);
    }
    prevLocationRef.current = location;
  }, [location.pathname, location.search, location, pageType, onPathChange]);

  const profileSkinActive = isProfileSkinActive(profileSkin, props.isDuperbrowseActive);

  const renderSaleBanner = useCallback(() => {
    const currentPromo = promos?.[0];
    if (!currentPromo) return null;
    const promoteBuyingCore =
      !profileOwner.isGroup &&
      // only show a self sale on the user's own page
      ((currentPromo.type === 'self' && isOwner) ||
        // only show a gift sale on other user's page
        (currentPromo.type === 'gift' && !isOwner && profileOwner.type === 'regular'));
    return (
      promoteBuyingCore && (
        <SaleBanner
          isNarrow={isMobile}
          className={s['sale-banner']}
          giftRecipient={currentPromo.type === 'gift' ? profileOwner.username : undefined}
          discountValuePercent={currentPromo.discountValuePercent}
          endDate={currentPromo.endDate}
          referralType={ReferralType.PROMOTION_BANNER_PROFILE}
        />
      )
    );
  }, [
    promos,
    profileOwner?.isGroup,
    profileOwner?.type,
    profileOwner?.username,
    isOwner,
    isMobile,
  ]);

  const renderProfileHeroEdit = useCallback(() => {
    if (!isAuthorizedToCustomize || isMobile) return null;
    return (
      <ProfileHeroEditControls
        className={classnames(
          s['profile-hero-edit'],
          showProfileHeroAid && s['show-ftue-controls']
        )}
        showProfileHeroAid={showProfileHeroAid}
      />
    );
  }, [isAuthorizedToCustomize, isMobile, showProfileHeroAid]);

  const renderSkinSelector = useCallback(() => {
    if (!isSkinSelectorShown) return null;
    return (
      <Suspense
        fallback={
          <LoadingIndicator
            coverMode
            view="llama"
            usePortal
            className={s['skin-selector-loading']}
          />
        }
      >
        <ProfileSkinSelector />
      </Suspense>
    );
  }, [isSkinSelectorShown]);

  const renderContent = useCallback(() => {
    const showEditControls = !doneShowingEditControls.current && isAuthorizedToCustomize;
    const dontUseOverflowAndStickyTogether =
      isMobile && ['posts', 'home', 'shop'].includes(activeSection);

    // show default profile background if the user is artist w/ no cover deviation or not editing cover deviation
    const showDefaultUserProfileBackground =
      !coverDeviation && !isHeroEditingActive && !profileOwner.isGroup;

    return (
      <div
        ref={pageContentContainer}
        className={classnames(
          s['content-container'],
          showEditControls && s['show-edit-controls'],
          showProfileHeroAid && s['show-ftue-controls'],
          isMobile && s['new-profile-hero']
        )}
      >
        <BackgroundContainer
          id="background-container"
          className={classnames(
            s['profile-hero-overlay'],
            showDefaultUserProfileBackground && s['default-hero-background'],
            showDefaultUserProfileBackground &&
              profileSkin?.skin?.baseTheme === 'dark' &&
              s['default-hero-background-dark'],
            showDefaultUserProfileBackground &&
              profileSkin?.skin?.baseTheme === 'light' &&
              s['default-hero-background-light']
          )}
          profileSkin={profileSkin}
          activeSection={activeSection}
        />
        <div className={s['content-container-with-paddings']}>
          <ErrorBoundary debugComponent="ProfileIndentity">
            <div className={s['profile-identity-container']}>
              {isMobile ? (
                <ProfileHeroMobile />
              ) : (
                <ProfileIdentity className={s['profile-identity']} />
              )}
            </div>
          </ErrorBoundary>
          <SubNavBar />
          <div
            ref={sectionContentContainer}
            className={classnames(
              s['section-container'],
              // removes the overflow: hidden from this element so that the sticky
              // positioned 'next post' button will properly follow the post as you scroll
              dontUseOverflowAndStickyTogether && s['no-overflow-prevention']
            )}
          >
            {renderSaleBanner()}
            {renderRoutes(routes)}
          </div>
        </div>
        {renderProfileHeroEdit()}
        {renderSkinSelector()}
      </div>
    );
  }, [
    isAuthorizedToCustomize,
    isMobile,
    activeSection,
    coverDeviation,
    isHeroEditingActive,
    profileOwner?.isGroup,
    showProfileHeroAid,
    profileSkin,
    renderSaleBanner,
    renderProfileHeroEdit,
    renderSkinSelector,
  ]);

  const renderProfilePage = useCallback(() => {
    const profileOwnerId = profileOwner.userId;

    return (
      <SectionProvider value={activeSection}>
        <BiLoggerContextProvider
          value={{
            sectionname: 'profile',
            typeid: ResourceType.USER,
            section_typeid: ResourceType.USER,
            page_typeid: ResourceType.USER,
            itemid: profileOwnerId,
            page_itemid: profileOwnerId,
          }}
        >
          <ConditionalWrapper
            condition={profileSkinActive}
            wrap={child => (
              <ThemeOverride theme={theme} className={themeSkin}>
                {child}
              </ThemeOverride>
            )}
          >
            <SiteHeader />
          </ConditionalWrapper>

          {isHeroEditingActive ? (
            <DraggableProfileHero className={s['profile-hero']} />
          ) : (
            !isMobile && (
              <ProfileHero
                showProfileHeroAid={showProfileHeroAid}
                className={s['profile-hero']}
                isGroup={profileOwner.isGroup}
              />
            )
          )}
          <div
            className={classnames(
              s['above-hero-content'],
              isHeroEditingActive && s['editing-hero']
            )}
          >
            {renderContent()}
            {!isHeroEditingActive && !isMobile && <MonetizationPromoBanner />}
            <SiteFooter className={s['special-profile-footer']} />
          </div>
        </BiLoggerContextProvider>
      </SectionProvider>
    );
  }, [
    profileOwner?.userId,
    profileOwner?.isGroup,
    activeSection,
    profileSkinActive,
    isHeroEditingActive,
    isMobile,
    showProfileHeroAid,
    renderContent,
    theme,
    themeSkin,
  ]);

  const renderNonProfilePage = useCallback(() => {
    return (
      <ErrorBoundary debugComponent="NonProfilePage">
        <SiteHeader />
        {pageType === 'groupCreation' && <GroupCreation />}
        <SiteFooter className={s['special-profile-footer']} />
      </ErrorBoundary>
    );
  }, [pageType]);

  return (
    <MuroDepContext.Provider value={MuroDepValue}>
      <GruserContext.Provider value={{ gruser, gruserOwner: profileOwner }}>
        <DaApp
          theme={profileSkinActive ? profileSkin?.skin?.baseTheme : undefined}
          /* We force the themeSkin off when the skin is active, if they add green based skins pass the skin here instead of NONE */
          themeSkin={profileSkinActive ? ThemeSkin.NONE : themeSkin}
          language={language}
          environment={environment}
          bodyClassName={classnames(isHeroEditingActive && s['disable-scroll'])}
          appUrl={appUrl}
          ncContextProps={{
            PageComponent: FeedbackPageLoadable,
            DrawerComponent: DrawerLoadable,
          }}
          damzContextProps={{
            GlobalComponent: DamzGlobal,
            DrawerComponent: DamzDrawerLoadable,
            PageComponent: DamzPageLoadable,
          }}
          modalsRenderFunc={composeModalRenderers(
            modalRenderer,
            adoptsModalRenderer,
            submitModalRenderer,
            muroModalRenderer,
            feedbackModalRenderer,
            damzModalRenderer as any
          )}
        >
          {pageType === 'profile' ? renderProfilePage() : renderNonProfilePage()}
          {PWAInstallBannerEligible && <PWAInstallBanner />}
        </DaApp>
      </GruserContext.Provider>
    </MuroDepContext.Provider>
  );
}

export default App;
